import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageSquare, Handshake, Home } from "lucide-react";
import { format } from "date-fns";
import { usePageTitle } from "@/hooks/use-page-title";
import { ConversationChat } from "@/components/conversation-chat";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

type ConnectionItem = {
  source: "partner" | "inquiry";
  id: number;
  title: string;
  counterpartyName: string;
  typeLabel: string;
  lastMessageAt: string;
  lastMessagePreview: string | null;
  unreadCount: number;
  listingId?: number;
  listingStatus?: string;
};

function itemKey(i: { source: string; id: number }) {
  return `${i.source}-${i.id}`;
}

export default function AppConnections() {
  usePageTitle("Connections");
  const [, navigate] = useLocation();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const meQuery = useQuery<{ user: { id: number } }>({ queryKey: ["/api/app/auth/me"] });
  const currentUserId = meQuery.data?.user?.id ?? null;

  const connectionsQuery = useQuery<ConnectionItem[]>({
    queryKey: ["/api/app/connections"],
    refetchInterval: 15000,
  });
  const items = connectionsQuery.data || [];

  const active = items.find((i) => itemKey(i) === selectedKey) || null;

  // Auto-select the first conversation on first load (desktop master-detail).
  useEffect(() => {
    if (!selectedKey && items.length > 0) {
      setSelectedKey(itemKey(items[0]));
    }
  }, [items.length, selectedKey]);

  // Deep-link support: ?source=partner&id=12
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get("source");
    const id = parseInt(params.get("id") || "", 10);
    if (source && !isNaN(id) && items.length > 0) {
      const match = items.find((i) => i.source === source && i.id === id);
      if (match) setSelectedKey(itemKey(match));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-y-3">
          <div data-tour="connections-heading">
            <h1 className="text-2xl font-bold text-slate-900" data-testid="text-connections-title">
              Connections
            </h1>
            <p className="text-slate-500 text-sm">
              Every conversation with partners and other hosts in one place — lenders, realtors,
              brokers, wholesalers, and marketplace buyers &amp; sellers.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="connections" compactOnMobile />
            <GuideButton pageKey="connections" compactOnMobile />
          </div>
        </div>
        <GuideNudgeBanner pageKey="connections" />

        {connectionsQuery.isLoading ? (
          <div className="space-y-3 max-w-md">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Master list */}
            <div className="md:col-span-1 border border-gray-200 rounded-xl bg-white overflow-hidden flex flex-col h-[540px]" data-tour="connections-inbox">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="font-medium text-gray-900">Inbox</h2>
                <p className="text-xs text-gray-500">
                  {items.length} {items.length === 1 ? "conversation" : "conversations"}
                </p>
              </div>
              {items.length === 0 ? (
                <div
                  className="flex-1 flex flex-col items-center justify-center text-center text-gray-500 px-4"
                  data-testid="connections-empty"
                >
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <MessageSquare className="h-7 w-7 text-gray-400" />
                  </div>
                  <p className="text-base font-medium text-gray-700">No conversations yet</p>
                  <p className="text-sm mt-1">
                    Connect with a partner from the Finance tab, or message a host from the
                    Buy/Sell marketplace to start a conversation.
                  </p>
                </div>
              ) : (
              <ul className="flex-1 overflow-y-auto" data-testid="list-connections">
                {items.map((i) => {
                  const isActive = itemKey(i) === selectedKey;
                  const Icon = i.source === "partner" ? Handshake : Home;
                  return (
                    <li key={itemKey(i)}>
                      <button
                        onClick={() => setSelectedKey(itemKey(i))}
                        className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${
                          isActive ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                        }`}
                        data-testid={`connection-row-${i.source}-${i.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 min-w-0">
                            <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{i.title}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {i.counterpartyName} · {i.typeLabel}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            {i.unreadCount > 0 && (
                              <Badge className="bg-blue-600 text-white border-0 text-[10px]" data-testid={`connection-unread-${i.source}-${i.id}`}>
                                {i.unreadCount}
                              </Badge>
                            )}
                            <span className="text-[10px] text-gray-400">
                              {format(new Date(i.lastMessageAt), "MMM d")}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 truncate pl-6">
                          {i.lastMessagePreview || "No messages yet"}
                        </p>
                      </button>
                    </li>
                  );
                })}
              </ul>
              )}
            </div>

            {/* Detail / inline chat */}
            <div className="md:col-span-2 space-y-3" data-tour="connections-detail">
              {active ? (
                <>
                  <div className="border border-gray-200 rounded-lg bg-white px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{active.title}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {active.counterpartyName} · {active.typeLabel}
                      </p>
                    </div>
                    {active.source === "inquiry" && active.listingId != null && (
                      <Button
                        size="sm"
                        variant="outline"
                        asChild
                        data-testid={`button-view-listing-${active.listingId}`}
                      >
                        <Link href={`/app/sell/${active.listingId}`}>View Listing</Link>
                      </Button>
                    )}
                  </div>
                  <ConversationChat
                    key={itemKey(active)}
                    source={active.source}
                    conversationId={active.id}
                    currentUserId={currentUserId}
                    counterpartyName={active.counterpartyName}
                    heightClass="h-[480px]"
                    disabled={active.source === "inquiry" && active.listingStatus !== "active"}
                    disabledPlaceholder="This listing is no longer accepting messages."
                  />
                </>
              ) : (
                <div className="border border-gray-200 rounded-xl bg-white p-8 text-center text-gray-500 h-[540px] flex flex-col items-center justify-center">
                  <MessageSquare className="h-10 w-10 text-gray-300 mb-2" />
                  <p className="text-sm">Select a conversation to view messages.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
